import React from 'react';
import { useNavigate } from 'react-router-dom';

interface EmptyStateProps {
  icon?: string;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  };
  className?: string;
}

// Base empty state component - Mobile Optimized
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon = '🔍',
  title,
  description,
  action,
  className = ''
}) => (
  <div className={`text-center py-8 sm:py-16 mobile-padding ${className}`}>
    <div className="max-w-md mx-auto">
      {/* Icon */}
      <div className="text-4xl sm:text-6xl mb-4 sm:mb-6 opacity-50">
        {icon}
      </div>
      
      {/* Title */}
      <h3 className="text-lg sm:text-xl font-semibold text-urban mb-2 sm:mb-3">
        {title}
      </h3>
      
      {/* Description */}
      <p className="text-sm sm:text-base text-urban-400 mb-6 sm:mb-8 leading-relaxed">
        {description}
      </p>
      
      {/* Action button */}
      {action && (
        <button
          onClick={action.onClick}
          className={`btn-mobile font-medium transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${
            action.variant === 'secondary'
              ? 'bg-white border-2 border-cardboard text-cardboard hover:bg-cardboard hover:text-white'
              : 'bg-cardboard hover:bg-cardboard-600 text-white'
          }`}
        >
          {action.label}
        </button>
      )}
    </div>
  </div>
);

// No treasures found (general) - internal component that uses navigate
const NoTreasuresFoundInternal: React.FC<{
  onSubmitTreasure?: () => void;
  showSubmitButton?: boolean;
}> = ({ onSubmitTreasure, showSubmitButton = true }) => {
  const navigate = useNavigate();
  
  const handleSubmitClick = () => {
    if (onSubmitTreasure) {
      onSubmitTreasure();
    } else {
      navigate('/submit');
    }
  };
  
  return (
    <EmptyState
      icon="🏴‍☠️"
      title="No treasures found"
      description="The treasure hunt continues! Try adjusting your search filters or explore a different area to discover hidden gems."
      action={showSubmitButton ? {
        label: 'Submit a Treasure',
        onClick: handleSubmitClick
      } : undefined}
    />
  );
};

// No treasures found (general) - exported component that handles navigation conditionally
export const NoTreasuresFound: React.FC<{
  onSubmitTreasure?: () => void;
  showSubmitButton?: boolean;
}> = ({ onSubmitTreasure, showSubmitButton = true }) => {
  // If callback is provided, don't use navigate
  if (onSubmitTreasure) {
    return (
      <EmptyState
        icon="🏴‍☠️"
        title="No treasures found"
        description="The treasure hunt continues! Try adjusting your search filters or explore a different area to discover hidden gems."
        action={showSubmitButton ? {
          label: 'Submit a Treasure',
          onClick: onSubmitTreasure
        } : undefined}
      />
    );
  }
  
  // Otherwise use the internal component with navigate
  return <NoTreasuresFoundInternal showSubmitButton={showSubmitButton} />;
};

// No search results
export const NoSearchResults: React.FC<{
  searchTerm?: string;
  onClearFilters?: () => void;
}> = ({ searchTerm, onClearFilters }) => (
  <EmptyState
    icon="🔍"
    title={searchTerm ? `No results for "${searchTerm}"` : "No results found"}
    description="We couldn't find any treasures matching your search criteria. Try different keywords or clear your filters to see more results."
    action={onClearFilters ? {
      label: 'Clear Filters',
      onClick: onClearFilters,
      variant: 'secondary'
    } : undefined}
  />
);

// No reports in area
export const NoReportsInArea: React.FC<{
  location?: string;
  onExpandSearch?: () => void;
}> = ({ location, onExpandSearch }) => (
  <EmptyState
    icon="🗺️"
    title={location ? `No treasures in ${location}` : "No treasures in this area"}
    description="This area hasn't been explored yet! Be the first treasure hunter to discover and share amazing finds with the community."
    action={onExpandSearch ? {
      label: 'Expand Search Area',
      onClick: onExpandSearch,
      variant: 'secondary'
    } : undefined}
  />
);

// No favorites
export const NoFavorites: React.FC = () => {
  const navigate = useNavigate();
  
  return (
    <EmptyState
      icon="❤️"
      title="No favorites yet"
      description="Start building your treasure collection! Browse available items and tap the heart icon to save your favorites."
      action={{
        label: 'Browse Treasures',
        onClick: () => navigate('/treasures')
      }}
    />
  );
};

// No submissions
export const NoSubmissions: React.FC = () => {
  const navigate = useNavigate();
  
  return (
    <EmptyState
      icon="📤"
      title="No submissions yet"
      description="Ready to become a treasure planter? Share your first curbside discovery and start earning coins and badges!"
      action={{
        label: 'Submit Your First Treasure',
        onClick: () => navigate('/submit')
      }}
    />
  );
};

// No updates
export const NoUpdates: React.FC<{
  onAddUpdate?: () => void;
}> = ({ onAddUpdate }) => (
  <EmptyState
    icon="📝"
    title="No updates yet"
    description="Be the first to provide an update on this treasure! Let the community know if it's still available or if anything has changed."
    action={onAddUpdate ? {
      label: 'Add Update',
      onClick: onAddUpdate
    } : undefined}
  />
);

// No notifications
export const NoNotifications: React.FC = () => (
  <EmptyState
    icon="🔔"
    title="All caught up!"
    description="You don't have any new notifications. Keep exploring and contributing to stay updated on community activity."
    className="py-12"
  />
);

// No admin reports (for moderation queue)
export const NoReportsToModerate: React.FC = () => (
  <EmptyState
    icon="✅"
    title="No reports to review"
    description="Great job! All reports have been reviewed. The community is running smoothly thanks to your moderation efforts."
    className="py-12"
  />
);

// Network error state
export const NetworkError: React.FC<{
  onRetry?: () => void;
}> = ({ onRetry }) => (
  <EmptyState
    icon="📡"
    title="Connection problem"
    description="We're having trouble connecting to our servers. Please check your internet connection and try again."
    action={onRetry ? {
      label: 'Try Again',
      onClick: onRetry
    } : undefined}
  />
);

// Maintenance mode
export const MaintenanceMode: React.FC = () => (
  <EmptyState
    icon="🔧"
    title="Under maintenance"
    description="We're making some improvements to enhance your treasure hunting experience. Please check back in a few minutes."
    className="py-20"
  />
);

// Access denied
export const AccessDenied: React.FC<{
  message?: string;
  onLogin?: () => void;
}> = ({ message, onLogin }) => (
  <EmptyState
    icon="🔒"
    title="Access restricted"
    description={message || "You need to be logged in to access this feature. Join our community of treasure hunters today!"}
    action={onLogin ? {
      label: 'Sign In',
      onClick: onLogin
    } : undefined}
  />
);

// Loading failed
export const LoadingFailed: React.FC<{
  onRetry?: () => void;
  error?: string;
}> = ({ onRetry, error }) => (
  <EmptyState
    icon="⚠️"
    title="Something went wrong"
    description={error || "We encountered an unexpected error while loading this content. Please try again."}
    action={onRetry ? {
      label: 'Retry',
      onClick: onRetry
    } : undefined}
  />
);

// Coming soon
export const ComingSoon: React.FC<{
  feature?: string;
}> = ({ feature = 'This feature' }) => (
  <EmptyState
    icon="🚀"
    title="Coming soon"
    description={`${feature} is currently under development. We're working hard to bring you new and exciting features!`}
    className="py-12"
  />
);

// First time user
export const FirstTimeUser: React.FC<{
  onGetStarted?: () => void;
}> = ({ onGetStarted }) => {
  const navigate = useNavigate();
  
  return (
    <EmptyState
      icon="👋"
      title="Welcome to Curbside Quest!"
      description="Ready to start your treasure hunting adventure? Discover amazing curbside finds, share your discoveries, and earn rewards!"
      action={{
        label: 'Get Started',
        onClick: onGetStarted || (() => navigate('/treasures'))
      }}
    />
  );
};

// Subscription required
export const SubscriptionRequired: React.FC<{
  feature?: string;
  onUpgrade?: () => void;
}> = ({ feature = 'This feature', onUpgrade }) => (
  <EmptyState
    icon="⭐"
    title="Premium feature"
    description={`${feature} is available to premium members. Upgrade your account to unlock extended location access and exclusive features.`}
    action={onUpgrade ? {
      label: 'Upgrade Now',
      onClick: onUpgrade
    } : undefined}
  />
);

// Tokens required
export const TokensRequired: React.FC<{
  onGetTokens?: () => void;
}> = ({ onGetTokens }) => (
  <EmptyState
    icon="🎫"
    title="Far-Away Tokens needed"
    description="You need Far-Away Tokens to access treasures in this area. Earn tokens through contributions or upgrade your subscription."
    action={onGetTokens ? {
      label: 'Get Tokens',
      onClick: onGetTokens
    } : undefined}
  />
);